import React, { useState } from 'react';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { AlertTriangle, Mail, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailVerificationBanner() {
  const user = useAuthStore((s) => s.user);
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('eb_email_banner_dismissed') === 'true');

  // Don't show banner if user is verified, not logged in, or banner dismissed
  if (!user || user.email_verified || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('eb_email_banner_dismissed', 'true');
  };

  const handleResend = async () => {
    setSending(true);
    try {
      const response = await api.post('/auth/resend-verification', {});
      if (response.data.email_sent) {
        toast.success('Verification email sent! Please check your inbox.');
      } else {
        toast.info('Email service not configured. Please contact support.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="email-verification-banner">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-sm text-amber-800">
              <span className="font-medium">Verify your email</span>
              {' '}- Verify your email to receive notifications and password reset links. All app features remain available.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResend}
              disabled={sending}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors disabled:opacity-50"
              data-testid="resend-verification-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-1.5" />
                  Resend Email
                </>
              )}
            </button>
            <button
              onClick={() => handleDismiss()}
              className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
